import { Input } from 'components/atoms';
import { FormInput, LabeledStatus, Tab, TabsNavigation } from 'components/molecules';
import FormSubmitButtons from 'components/molecules/buttons/formSubmitButtons/FormSubmitButtons';
import Tabs from 'components/molecules/tabs';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useGetGenres, useGetLanguages } from 'services/enums';
import { RELEASE_TYPE } from 'services/music-product-physical/types/enums';
import { IMusicProductPhysicalForm } from 'services/music-product-physical/types/form';
import { ARTIST_ROLE, GENRE, STATUS } from 'services/types';
import { II18nextPrefix } from 'types/components/components.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import {
  conditionalField,
  credensialsValidation,
  externalIdsValidation,
  requiredEnumSelect,
  titlesValidation,
} from 'utils/global/validation.utils';
import { array, boolean, date, mixed, number, object, string } from 'yup';
import { IFormProps, IFormikProps } from '../types';
import MusicProductPhysicalData from './tabs/MusicProductPhysicalData';
import { albumValidation } from './tabs/MusicProductPhysicalTracks';

export enum musicProductPhysicalTabs {
  contracts,
  tracks,
  artists,
  titles,
  external,
  data,
}

export const musicProductPhysicalCopyrights = {
  name: '',
  year: '',
};

export const musicProductPhysicalInitialValues: IMusicProductPhysicalForm = {
  mainTitle: {
    language: -1,
    title: '',
    version: '',
  },
  releaseStatus: STATUS.draft,
  releaseType: RELEASE_TYPE.unknown,
  releaseDate: '',
  genre: { id: -1, name: '' },
  ean: '',
  pLine: musicProductPhysicalCopyrights,
  cLine: { name: '', year: '' },
  productImageLink: '',
  productImagePreviewLink: '',
  artists: [],
  externalIds: [],
  otherTitles: [],
  tracks: [],
  settlementInfo: {
    contracts: [],
    perTrackSettlement: false,
    perTrackContracts: [],
  },
  version: 0,
};

export const artistsValidation = object().shape({
  id: string().required(() => i18next.t('formik-error.required')),
  role: requiredEnumSelect(ARTIST_ROLE),
});
export const settlementInfoContractValidation = object().shape({
  contractId: string()
    .trim()
    .required(() => i18next.t('formik-error.required')),
  labelId: string().required(() => i18next.t('formik-error.required')),
  startDate: string().required(() => i18next.t('formik-error.required')),
  divisionPercentage: number().required(() => i18next.t('formik-error.required')),
});
export const settlementInfoPerTrackContractValidation = object().shape({
  contracts: array().of(settlementInfoContractValidation),
});
export const settlementInfoValidation = object().shape({
  perTrackSettlement: boolean().when('contracts', {
    is: (contracts: any[]) => !!contracts.length,
    then: (schema) => schema.required(() => i18next.t('formik-error.required')),
    otherwise: (schema) => schema.nullable(),
  }),
  contracts: array().of(settlementInfoContractValidation),
  perTrackContracts: array().of(settlementInfoPerTrackContractValidation),
});

const musicProductPhysicalValidationSchema = object().shape({
  releaseStatus: requiredEnumSelect(STATUS),
  mainTitle: titlesValidation,
  pLine: credensialsValidation('releaseStatus'),
  cLine: credensialsValidation('releaseStatus'),
  ean: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    'releaseStatus',
  ),
  releaseType: conditionalField(requiredEnumSelect(RELEASE_TYPE), 'releaseStatus'),
  releaseDate: conditionalField(
    date().required(() => i18next.t('formik-error.required')),
    'releaseStatus',
  ),
  genre: conditionalField(requiredEnumSelect(GENRE), 'releaseStatus'),
  artists: array()
    .of(artistsValidation)
    .when('releaseStatus', {
      is: (status?: STATUS) => status !== STATUS.draft && status !== STATUS.unknown,
      then: (schema) =>
        schema
          .min(1, () => i18next.t('formik-error.at-least-one-artist-required'))
          .test(
            'has-mpd-main-artist',
            () =>
              i18next.t('formik-error.what-should-have-main-artist', {
                what: i18next.t('track.what'),
              }),
            (artist) => artist && artist.some((item) => item.role === ARTIST_ROLE.mainArtist),
          ),
      otherwise: (schema) => schema,
    }),
  otherTitles: array().of(titlesValidation),
  externalIds: array().of(externalIdsValidation),
  tracks: array()
    .of(albumValidation)
    .when('releaseStatus', {
      is: (status?: STATUS) => status !== STATUS.draft && status !== STATUS.unknown,
      then: (schema) =>
        schema
          .min(1, () => i18next.t('formik-error.at-least-one-album-required'))
          .required(() => i18next.t('formik-error.required')),
      otherwise: (schema) => schema,
    }),
  productImageLink: string().nullable(),
  file: conditionalField(
    mixed().when('productImageLink', {
      is: (value?: string) => !!value?.length,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required(() => i18next.t('formik-error.required')),
    }),
    'releaseStatus',
  ),
  settlementInfo: settlementInfoValidation,
});

export interface IMusicProductPhysicalFormProps
  extends
    Omit<FormikConfig<IMusicProductPhysicalForm>, 'initialValues'>,
    IFormProps,
    II18nextPrefix {
  initialValues?: IMusicProductPhysicalForm;
}

const MusicProductPhysicalForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = musicProductPhysicalInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  validationSchema = musicProductPhysicalValidationSchema,
  ...props
}: IMusicProductPhysicalFormProps) => {
  const { t } = useTranslation();
  const { getSelectOptions, getStatusOptions } = useEnumToSelectOptions();

  const languages = useGetLanguages();

  const genres = useGetGenres();

  return (
    <Formik
      {...props}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}
    >
      {({
        setFieldValue,
        values,
        status,
        errors,
        handleBlur,
      }: IFormikProps<IMusicProductPhysicalForm>) => (
        <Form className="w-full h-full">
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status?.displayMode ? tittle : undefined}
              path={backPath ?? 'music-product-physical'}
              topbarContent={
                <div className="flex w-full items-center justify-end">
                  {status?.displayMode ? null : (
                    <Input.ComplexWrapper sizes={[50, 50]} className="max-w-[500px]">
                      <FormInput.Text
                        name="mainTitle.title"
                        label={t('input.label.title')}
                        autoFocus
                        appBackground
                      />
                      <FormInput.Text
                        name="mainTitle.version"
                        label={t('input.label.version')}
                        appBackground
                      />
                    </Input.ComplexWrapper>
                  )}
                  <div className="flex items-center gap-2 w-full justify-end">
                    {status?.displayMode ? (
                      <LabeledStatus status={initialValues.releaseStatus} />
                    ) : (
                      <FormSubmitButtons statusName="releaseStatus" />
                    )}
                    {topbarContent}
                  </div>
                </div>
              }
            >
              <TabsNavigation
                disabled={[...(disableTabs ?? [])]}
                tabs={[
                  {
                    value: musicProductPhysicalTabs.data,

                    name: 'data',
                  },
                  {
                    value: musicProductPhysicalTabs.tracks,
                    error: errors.tracks,
                    name: 'tracks',
                  },
                  {
                    value: musicProductPhysicalTabs.artists,
                    error: errors.artists,
                    name: 'artists',
                  },
                  {
                    value: musicProductPhysicalTabs.contracts,
                    name: 'contracts',
                    error: errors.settlementInfo?.contracts,
                  },
                  {
                    value: musicProductPhysicalTabs.titles,
                    error: errors.otherTitles,
                    name: 'titles',
                  },
                  {
                    value: musicProductPhysicalTabs.external,
                    error: errors.externalIds,
                    name: 'external',
                  },
                ]}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

MusicProductPhysicalForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'contracts', element: <Tab.ContractsPerTrack /> },
    { path: 'external', element: <Tab.ExternalId parent="MUSIC_PRODUCT_PHYSICAL" /> },
    // { path: "tracks", element: <MusicProductPhysicalTracks /> },
    { path: 'tracks', element: <Tabs.Tracks /> },
    { path: 'data', element: <MusicProductPhysicalData /> },
    { path: 'artists', element: <Tabs.ArtistsRole /> },
    { path: 'titles', element: <Tabs.Titles parent="MUSIC_PRODUCT_PHYSICAL" /> },
  ].filter((children) => !disabledChildren?.includes(children.path));

export default MusicProductPhysicalForm;
